import React, { SetStateAction } from 'react'
import styles from './AddProperty.module.css';
import HistoryIcon from '../../svgIcons/HistoryIcon';
import CloseIcon from '../../svgIcons/CloseIcon';
import moment from 'moment';

interface LogProps {
    id: string,
    day: string,
    isOpen: boolean | number,
    openTime: string | null,
    closeTime: string | null,
    created_at: string | null,
    locationId: string,
    locationName: string
}

interface HistoryProps {
    logId: string,
    table_name: string,
    action: string,
    new_values: LogProps[],
    old_values: LogProps[],
    createdAt: string,
    updatedAt: string,
    record_id: string,
    adminName: string,
    adminEmail: string
}

interface HistoryDataProps {
    data: HistoryProps[];
    setShowLogsModal: React.Dispatch<SetStateAction<boolean>>
}

const dayOrder: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};

const sortLogsByDay = (logs: LogProps[]) => {
    return [...logs].sort((a, b) => {
        return (dayOrder[a.day] ?? 7) - (dayOrder[b.day] ?? 7);
    });
};

const hasScheduleChanged = (oldLog?: LogProps, newLog?: LogProps): boolean => {
    if (!oldLog && !newLog) return false;
    if (!oldLog || !newLog) return true;

    return (
        oldLog.day !== newLog.day ||
        Boolean(oldLog.isOpen) !== Boolean(newLog.isOpen) ||
        (Boolean(oldLog.isOpen) && Boolean(newLog.isOpen) && (
            oldLog.openTime !== newLog.openTime ||
            oldLog.closeTime !== newLog.closeTime
        ))
    );
};

// Checks if ANY day in the week has changed
const hasAnyDayChanged = (oldLogs: LogProps[], newLogs: LogProps[]): boolean => {
    const maxLen = Math.max(oldLogs.length, newLogs.length);
    for (let i = 0; i < maxLen; i++) {
        if (hasScheduleChanged(oldLogs[i], newLogs[i])) {
            return true;
        }
    }
    return false;
};

const HistoryModalBody = ({ data, setShowLogsModal }: HistoryDataProps) => {

    return (
        <div className={styles.logsModalContainer}>
            <div className={styles.modalHeader}>
                <div className={styles.inlineItems}>
                    <HistoryIcon />
                    <h2>Office Hours History</h2>
                </div>
                <button className={styles.closeBtnWrapper}
                    onClick={() => setShowLogsModal(false)}
                >
                    <CloseIcon />
                </button>
            </div>

            <div className={styles.modalBody}>
                <table >
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Date</th>
                            <th>Old values</th>
                            <th>New values</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!!data.length && data.map((value: HistoryProps, index: number) => {
                            const now = moment();
                            const date = moment(value.updatedAt);
                            const daysAgo = now.diff(date, 'days');
                            const oldLogs = sortLogsByDay(value.old_values);
                            const newLogs = sortLogsByDay(value.new_values);

                            const hasAnyChanges = hasAnyDayChanged(oldLogs, newLogs);
                            if (!hasAnyChanges) return null;

                            if (hasAnyChanges) {
                                return (
                                    <tr key={index}>
                                        <td>
                                            <div>
                                                <p>{value.adminName}</p>
                                                <p>{value.adminEmail}</p>
                                            </div>
                                        </td>

                                        <td>
                                            <p>{moment(value.updatedAt).locale('en').format('MMM DD,yyyy hh:mm A')}</p>
                                            <p>{daysAgo ? `${daysAgo} days ago` : 'Today'}</p>
                                        </td>

                                        <td>
                                            <div>
                                                {(Boolean(value.old_values[0]?.isOpen) !== Boolean(value.new_values[0]?.isOpen) ||
                                                    (Boolean(value.old_values[0]?.isOpen) &&
                                                        Boolean(value.new_values[0]?.isOpen) &&
                                                        (value.old_values[0].openTime !== value.new_values[0].openTime ||
                                                            value.old_values[0].closeTime !== value.new_values[0].closeTime))) && (
                                                        <p>
                                                            <span>Sunday: </span>
                                                            <span>
                                                                {!value.old_values[0]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[0].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[0].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[1]?.isOpen) !== Boolean(value.new_values[1]?.isOpen) ||
                                                    (Boolean(value.old_values[1]?.isOpen) &&
                                                        Boolean(value.new_values[1]?.isOpen) &&
                                                        (value.old_values[1].openTime !== value.new_values[1].openTime ||
                                                            value.old_values[1].closeTime !== value.new_values[1].closeTime))) && (
                                                        <p>
                                                            <span>Monday: </span>
                                                            <span>
                                                                {!value.old_values[1]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[1].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[1].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[2]?.isOpen) !== Boolean(value.new_values[2]?.isOpen) ||
                                                    (Boolean(value.old_values[2]?.isOpen) &&
                                                        Boolean(value.new_values[2]?.isOpen) &&
                                                        (value.old_values[2].openTime !== value.new_values[2].openTime ||
                                                            value.old_values[2].closeTime !== value.new_values[2].closeTime))) && (
                                                        <p>
                                                            <span>Tuesday: </span>
                                                            <span>
                                                                {!value.old_values[2]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[2].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[2].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[3]?.isOpen) !== Boolean(value.new_values[3]?.isOpen) ||
                                                    (Boolean(value.old_values[3]?.isOpen) &&
                                                        Boolean(value.new_values[3]?.isOpen) &&
                                                        (value.old_values[3].openTime !== value.new_values[3].openTime ||
                                                            value.old_values[3].closeTime !== value.new_values[3].closeTime))) && (
                                                        <p>
                                                            <span>Wednesday: </span>
                                                            <span>
                                                                {!value.old_values[3]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[3].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[3].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[4]?.isOpen) !== Boolean(value.new_values[4]?.isOpen) ||
                                                    (Boolean(value.old_values[4]?.isOpen) &&
                                                        Boolean(value.new_values[4]?.isOpen) &&
                                                        (value.old_values[4].openTime !== value.new_values[4].openTime ||
                                                            value.old_values[4].closeTime !== value.new_values[4].closeTime))) && (
                                                        <p>
                                                            <span>Thursday: </span>
                                                            <span>
                                                                {!value.old_values[4]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[4].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[4].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[5]?.isOpen) !== Boolean(value.new_values[5]?.isOpen) ||
                                                    (Boolean(value.old_values[5]?.isOpen) &&
                                                        Boolean(value.new_values[5]?.isOpen) &&
                                                        (value.old_values[5].openTime !== value.new_values[5].openTime ||
                                                            value.old_values[5].closeTime !== value.new_values[5].closeTime))) && (
                                                        <p>
                                                            <span>Friday: </span>
                                                            <span>
                                                                {!value.old_values[5]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[5].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[5].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[6]?.isOpen) !== Boolean(value.new_values[6]?.isOpen) ||
                                                    (Boolean(value.old_values[6]?.isOpen) &&
                                                        Boolean(value.new_values[6]?.isOpen) &&
                                                        (value.old_values[6].openTime !== value.new_values[6].openTime ||
                                                            value.old_values[6].closeTime !== value.new_values[6].closeTime))) && (
                                                        <p>
                                                            <span>Saturday: </span>
                                                            <span>
                                                                {!value.old_values[6]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.old_values[6].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.old_values[6].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                            </div>
                                        </td>

                                        <td>
                                            <div>
                                                {(Boolean(value.old_values[0]?.isOpen) !== Boolean(value.new_values[0]?.isOpen) ||
                                                    (Boolean(value.old_values[0]?.isOpen) &&
                                                        Boolean(value.new_values[0]?.isOpen) &&
                                                        (value.old_values[0].openTime !== value.new_values[0].openTime ||
                                                            value.old_values[0].closeTime !== value.new_values[0].closeTime))) && (
                                                        <p>
                                                            <span>Sunday: </span>
                                                            <span>
                                                                {!value.new_values[0]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[0].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[0].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[1]?.isOpen) !== Boolean(value.new_values[1]?.isOpen) ||
                                                    (Boolean(value.old_values[1]?.isOpen) &&
                                                        Boolean(value.new_values[1]?.isOpen) &&
                                                        (value.old_values[1].openTime !== value.new_values[1].openTime ||
                                                            value.old_values[1].closeTime !== value.new_values[1].closeTime))) && (
                                                        <p>
                                                            <span>Monday: </span>
                                                            <span>
                                                                {!value.new_values[1]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[1].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[1].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[2]?.isOpen) !== Boolean(value.new_values[2]?.isOpen) ||
                                                    (Boolean(value.old_values[2]?.isOpen) &&
                                                        Boolean(value.new_values[2]?.isOpen) &&
                                                        (value.old_values[2].openTime !== value.new_values[2].openTime ||
                                                            value.old_values[2].closeTime !== value.new_values[2].closeTime))) && (
                                                        <p>
                                                            <span>Tuesday: </span>
                                                            <span>
                                                                {!value.new_values[2]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[2].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[2].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[3]?.isOpen) !== Boolean(value.new_values[3]?.isOpen) ||
                                                    (Boolean(value.old_values[3]?.isOpen) &&
                                                        Boolean(value.new_values[3]?.isOpen) &&
                                                        (value.old_values[3].openTime !== value.new_values[3].openTime ||
                                                            value.old_values[3].closeTime !== value.new_values[3].closeTime))) && (
                                                        <p>
                                                            <span>Wednesday: </span>
                                                            <span>
                                                                {!value.new_values[3]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[3].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[3].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[4]?.isOpen) !== Boolean(value.new_values[4]?.isOpen) ||
                                                    (Boolean(value.old_values[4]?.isOpen) &&
                                                        Boolean(value.new_values[4]?.isOpen) &&
                                                        (value.old_values[4].openTime !== value.new_values[4].openTime ||
                                                            value.old_values[4].closeTime !== value.new_values[4].closeTime))) && (
                                                        <p>
                                                            <span>Thursday: </span>
                                                            <span>
                                                                {!value.new_values[4]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[4].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[4].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[5]?.isOpen) !== Boolean(value.new_values[5]?.isOpen) ||
                                                    (Boolean(value.old_values[5]?.isOpen) &&
                                                        Boolean(value.new_values[5]?.isOpen) &&
                                                        (value.old_values[5].openTime !== value.new_values[5].openTime ||
                                                            value.old_values[5].closeTime !== value.new_values[5].closeTime))) && (
                                                        <p>
                                                            <span>Friday: </span>
                                                            <span>
                                                                {!value.new_values[5]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[5].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[5].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                {(Boolean(value.old_values[6]?.isOpen) !== Boolean(value.new_values[6]?.isOpen) ||
                                                    (Boolean(value.old_values[6]?.isOpen) &&
                                                        Boolean(value.new_values[6]?.isOpen) &&
                                                        (value.old_values[6].openTime !== value.new_values[6].openTime ||
                                                            value.old_values[6].closeTime !== value.new_values[6].closeTime))) && (
                                                        <p>
                                                            <span>Saturday: </span>
                                                            <span>
                                                                {!value.new_values[6]?.isOpen
                                                                    ? 'Closed'
                                                                    : `${moment(value.new_values[6].openTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                                        value.new_values[6].closeTime,
                                                                        'HH:mm',
                                                                    ).format('hh:mm A')}`}
                                                            </span>
                                                        </p>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                        })}
                    </tbody>
                </table>
            </div>
        </div >
    )
}

export default HistoryModalBody